import { DeleteOutlined, DownOutlined, EyeOutlined, UpOutlined } from '@ant-design/icons'
import type { AdCampaign } from '@entities/ad-campaign/model/types'
import type { Post } from '@entities/post'
import { AdMobilePost, EditAdsPostMobileModal, PreviewPostMobileModal, usePosts } from '@entities/post'
import { Button, Col, Flex, Row, Space, theme, Typography } from 'antd'
import { Dialog } from 'antd-mobile'
import { useState } from 'react'
import * as styles from './mobile-ad-campaign.module.scss'

const { Text, Title } = Typography

interface MobileAdCampaignProps {
    adCampaign: AdCampaign
    onDelete: () => void
}

export const MobileAdCampaign = ({ adCampaign, onDelete }: MobileAdCampaignProps) => {
    const {
        token: { colorPrimary, colorWhite }
    } = theme.useToken()

    const [collapsed, setCollapsed] = useState(true)

    const {
        adsPosts,
        actions: { deletePost, previewPost }
    } = usePosts({ adCampaignId: adCampaign.id })

    const onPostEdit = (post: Post) => onEditModalOpen(post)
    const onPostPreview = (post: Post) => onPreviewModalOpen(post)
    const onPostPreviewToBot = (post: Post) => previewPost(post)
    const onPostDelete = (id: number) => deletePost(id)

    const [editPostData, setEditPost] = useState<Post | null>(null)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [previewPostData, setPreviewPostData] = useState<Post | null>(null)
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

    const onEditModalOpen = (post: Post) => {
        setEditPost(post)
        setTimeout(() => {
            setIsEditModalOpen(true)
        }, 0)
    }

    const onEditModalClose = () => {
        setEditPost(null)
        setIsEditModalOpen(false)
    }

    const onPreviewModalOpen = (post: Post) => {
        setPreviewPostData(post)
        setTimeout(() => {
            setIsPreviewModalOpen(true)
        }, 0)
    }

    const onPreviewModalClose = () => {
        setPreviewPostData(null)
        setIsPreviewModalOpen(false)
    }

    return (
        <div className={styles.container}>
            <Flex justify="space-between">
                <Title level={5} style={{ marginTop: 0, textAlign: 'center', color: colorPrimary }}>
                    {adCampaign.name}
                </Title>

                <Space>
                    <Button
                        type="text"
                        onClick={() => adsPosts?.results[0] && onPreviewModalOpen(adsPosts?.results[0])}
                        className={styles.actionBtn}
                    >
                        <EyeOutlined style={{ color: colorPrimary }} />
                    </Button>
                    <Button
                        type="text"
                        onClick={() =>
                            Dialog.confirm({
                                content: 'Вы уверены что хотите удалить рекламную кампанию?',
                                cancelText: 'Отмена',
                                confirmText: 'Удалить',
                                onConfirm: onDelete
                            })
                        }
                        className={styles.actionBtn}
                    >
                        <DeleteOutlined style={{ color: colorPrimary }} />
                    </Button>
                </Space>
            </Flex>

            <Row style={{ position: 'relative' }}>
                <Col
                    span={12}
                    style={{ borderTop: '0.5px solid #FEFEFF', borderRight: '0.5px solid #FEFEFF', padding: '6px' }}
                >
                    <Space>
                        <Text style={{ color: colorPrimary, fontWeight: 600 }}>CPS чистый</Text>
                        <Text
                            style={{
                                color: colorPrimary,
                                border: '1px solid #7C9BAD',
                                borderRadius: '5px',
                                padding: '2px'
                            }}
                        >
                            -
                        </Text>
                    </Space>
                </Col>
                <Col span={12} style={{ borderTop: '0.5px solid #FEFEFF', padding: '6px' }}>
                    <Space>
                        <Text style={{ color: colorPrimary, fontWeight: 600 }}>Цена</Text>
                        <Text
                            style={{
                                color: colorPrimary,
                                border: '1px solid #7C9BAD',
                                borderRadius: '5px',
                                padding: '2px'
                            }}
                        >
                            {adCampaign.spent} Р
                        </Text>
                    </Space>
                </Col>
                <Col
                    span={12}
                    style={{ borderTop: '0.5px solid #FEFEFF', borderRight: '0.5px solid #FEFEFF', padding: '6px' }}
                >
                    <Space>
                        <Text style={{ color: colorPrimary, fontWeight: 600 }}>Интернет-ресурсы</Text>
                        <Text style={{ color: colorPrimary, fontWeight: 600 }}>{adCampaign.resourcesCount}</Text>
                    </Space>
                </Col>
                <Col span={12} style={{ borderTop: '0.5px solid #FEFEFF', padding: '6px' }}>
                    <Space>
                        <Text style={{ color: colorPrimary, fontWeight: 600 }}>Партнеры</Text>
                        <Text style={{ color: colorPrimary, fontWeight: 600 }}>{adCampaign.partnersCount}</Text>
                    </Space>
                </Col>
                <Button type="text" onClick={() => setCollapsed((prev) => !prev)} className={styles.expandBtn}>
                    {collapsed ? (
                        <DownOutlined style={{ color: colorWhite }} />
                    ) : (
                        <UpOutlined style={{ color: colorWhite }} />
                    )}
                </Button>
            </Row>

            {!collapsed && adsPosts?.results.length && (
                <Row style={{ marginTop: '16px' }} gutter={[16, 16]}>
                    {adsPosts?.results.map((post) => {
                        return (
                            <Col span={24} key={post.id}>
                                <AdMobilePost
                                    post={post}
                                    onEdit={onPostEdit}
                                    onPreview={onPostPreview}
                                    onPreviewToBot={onPostPreviewToBot}
                                    onDelete={() => onPostDelete(post.id)}
                                />
                            </Col>
                        )
                    })}
                </Row>
            )}

            {editPostData && (
                <EditAdsPostMobileModal
                    editPostData={editPostData}
                    open={isEditModalOpen}
                    onClose={onEditModalClose}
                    adCampaignName={adCampaign.name}
                />
            )}

            <PreviewPostMobileModal
                post={previewPostData}
                open={isPreviewModalOpen}
                onClose={onPreviewModalClose}
                onEditModalOpen={onEditModalOpen}
            />
        </div>
    )
}
